import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import * as Type from 'library/ts/utilities/modules/Type';

/**
 * INFO: CSS Operations
 */

/**
 * @description Checks if css class exists on element instance
 * @param element {Element|Node}
 * @param className {String} CSS class name
 * @return {Boolean}
 */
export const hasClass = ( element: HTMLElement, className: string ): boolean => {
	if ( Type.isNull( element.getAttribute( 'class' ) ) === true ) return false;

	if ( element.classList ) {
		return element.classList.contains( className );
	} else {
		return ( element.getAttribute( 'class' ).indexOf( className ) !== -1 );
	}
};

/**
 * @description Adds css class to element instance
 * @param element {Element|Node}
 * @param className {String} CSS class name
 */
export const addClass = ( element: HTMLElement, className: string ): void => {
	if ( Type.isElement( element ) === false ) return;
	if ( element.classList ) {
		element.classList.add( className );
	} else if ( !hasClass( element, className ) ) {
		element.setAttribute( 'class', ( element.getAttribute( 'class' ) + ' ' + className ) );
	}
};

/**
 * @description Removes css class from element instance
 * @param element {HTMLElement|Node}
 * @param className {String} CSS class name
 */
export const removeClass = ( element: HTMLElement, className: string ): void => {
	if ( Type.isElement( element ) === false ) return;
	if ( element.classList ) {
		element.classList.remove( className );
	} else if ( hasClass( element, className ) ) {
		const classes = element.getAttribute( 'class' ).replace( className, ' ' );
		element.setAttribute( 'class', classes.trim() );
	}
};

/**
 * @description Add css class if condition is true, else remove it
 * @param condition {boolean}
 * @param element {HTMLElement}
 * @param className {String} CSS class name
 */
export const toggleClass = ( condition: boolean, element: HTMLElement, className: string ): void => {
	// sanity check
	if ( Type.isElement( element ) ) {
		// process add/remove CSS class logic
		condition ? addClass( element, className ) : removeClass( element, className );
	}
};

/**
 * @description Toggle css classes depending on if breakpoints are met
 */
export const toggleBreakpoint = ( rect: DOMRectReadOnly, elements: HTMLElement[], breakpoints: ICssBreakpoint[] ) => {
	// console.info( 'toggleBreakpoint', 'rect:', rect, 'elements:', elements, 'breakpoints:', breakpoints );
	if ( Type.isArray( elements ) && !Type.isEmpty( elements ) && Type.isArray( breakpoints ) && !Type.isEmpty( breakpoints ) ) {
		breakpoints.forEach(
			( breakpoint ) => {
				const {
					minWidth, maxWidth, minHeight, maxHeight
				} = breakpoint;
				const meetsMinWidth = !minWidth || minWidth <= rect.width;
				const meetsMaxWidth = !maxWidth || maxWidth >= rect.width;
				const meetsMinHeight = !minHeight || minHeight <= rect.height;
				const meetsMaxHeight = !maxHeight || maxHeight >= rect.height;
				const toggle = meetsMinWidth && meetsMaxWidth && meetsMinHeight && meetsMaxHeight;

				elements.forEach(
					( el ) => {
						if ( Type.isElement( el ) ) {
							toggleClass( toggle, el, breakpoint?.className );
						}
					}
				);
			}
		);
	}
};

/**
 * @description Removes multiple classes
 * @param element {HTMLElement|Node}
 * @param classes {String} a string of space-separated classes 'class1 class2 class 3'
 */
export const removeClasses = ( element: HTMLElement, classes: string ): void => {
	classes = classes.trim();
	/**
	 * @type {Array}
	 */
	let classesProcessed;
	classes.indexOf( ' ' ) > -1 ? classesProcessed = classes.split( ' ' ) : classesProcessed = [ classes ];

	for ( let i = 0; i < classesProcessed.length; i++ ) {
		if ( hasClass( element, classesProcessed[ i ] ) ) removeClass( element, classesProcessed[ i ] );
	}
};

/**
 * @description Removes classes containing matching substring
 * @param element {Element|Node}
 * @param substring {String}
 */
export const removeClassesWithSubstring = ( element: HTMLElement, substring: string ): void => {
	let classes: any = element.className.trim(); // get list of classes
	classes.indexOf( ' ' ) > -1 ? classes = classes.split( ' ' ) : classes = [ classes ];

	for ( let i = 0; i < classes.length; i++ ) {
		if ( classes[ i ].indexOf( substring ) > -1 ) removeClass( element, classes[ i ] );
	}
};

/**
 * @description Removes all CSS classes from element
 * @param element {Element|Node}
 */
export const removeAllClasses = ( element: HTMLElement ): void => {
	element.className = '';
};

/**
 * @description Adds CSS selection class to an element based on value property
 * @param {string} value
 * @param {NodeListOf<HTMLElement | Element>} elements
 * @param {string} valueAttribute
 * @param {string} selectionClass
 */
export const setElementSelected = ( value: string, elements: NodeListOf<HTMLElement | Element>, valueAttribute: string = 'value', selectionClass: string = 'selected' ): void => {
	for ( let i = 0; i < elements.length; i++ ) {
		if ( elements[ i ].getAttribute( valueAttribute ) === value ) {
			addClass( elements[ i ] as HTMLElement, selectionClass );
		} else {
			removeClass( elements[ i ] as HTMLElement, selectionClass );
		}
	}
};

/**
 * @description Selects radio button (or other input element) with specific class name while deselecting others
 * @param cssClass {String}
 * @param nodeList {NodeList|Array.<HTMLInputElement>}
 */
export const selectInputElementByCssClass = ( cssClass: string = undefined, nodeList: HTMLInputElement[] = undefined ): void => {
	if ( Type.isUndefined( cssClass ) ) return;
	if ( Type.isEmptyString( cssClass ) ) return;
	// loop over element nodes
	if ( Type.isDefined( nodeList ) ) {
		for ( let i = 0; i < nodeList.length; i++ ) {
			nodeList[ i ].checked = hasClass( nodeList[ i ], cssClass );
		}
	}
};

/**
 * @description Adds CSS rule to DOM
 * @see {@link  https://davidwalsh.name/add-rules-stylesheets}
 * @example
 * ```typescript
 * addCSSRule( document.styleSheets[ 0 ], "header", "float: left" );
 * ```
 * @param sheet {Object}
 * @param selector {String}
 * @param rules {String} CSS string
 * @return {String} CSS text
 */
export const addCSSRule = ( sheet: any, selector: string, rules: string ): string => {
	// Backward searching of the selector matching cssRules
	let index = sheet.cssRules.length - 1;
	for ( let i = index; i > 0; i-- ) {
		const current_style = sheet.cssRules[ i ];
		if ( current_style.selectorText === selector ) {
			// Append the new rules to the current content of the cssRule;
			rules = current_style.style.cssText + rules;
			sheet.deleteRule( i );
			index = i;
		}
	}
	if ( sheet.insertRule ) {
		sheet.insertRule( selector + '{' + rules + '}', index );
	} else {
		sheet.addRule( selector, rules, index );
	}
	return sheet.cssRules[ index ].cssText;
};

/**
 * @description Injects CSS rules into DOM. Inspired by codepen.io/anon/pen/vtHCs
 * @see {@link https://codepen.io/anon/pen/vtHCs}
 * @param cssRules {String} CSS rules string
 * @param parent {String} Parent for css injection: 'head' or 'body'
 * @param divID {String} Container div id for 'body type injection'
 * @example
 * ```typescript
 * injectCSS(
 *    ".arrow_box_altered::after{ left: 50px; border-width: 14px; margin-left: -14px; }" +
 *    ".arrow_box_altered::before{ left: 50px; border-width: 20px; margin-left: -20px; }"
 * );
 * ```
 */
export const injectCSS = ( cssRules: string, parent: string = 'head', divID: string = 'smart-tag-injected-css' ): void => {
	switch ( parent ) {
		case 'head':
			const style = document.createElement( 'style' );
			style.type = 'text/css';
			style.innerHTML = cssRules;
			document.getElementsByTagName( 'head' )[ 0 ].appendChild( style );
			break;
		case 'body':
			// wrapper for all injected styles and temp el to create them
			const wrap = document.createElement( 'div' );
			const temp = document.createElement( 'div' );
			// append wrapper to the body on the first call
			if ( !wrap.id ) {
				wrap.id = divID;
				wrap.style.display = 'none';
				document.body.appendChild( wrap );
			}
			// <br> for IE: https://goo.gl/vLY4x7
			temp.innerHTML = '<br><style>' + cssRules + '</style>';
			wrap.appendChild( temp.children[ 1 ] );
			break;
	}
};

/**
 * @description Removes a property from inline style
 * @param element {HTMLElement}
 * @param property {String}
 */
export const removeInlineStyleProperty = ( element: HTMLDivElement | any, property: string ): void => {
	if ( element.style.removeProperty ) {
		element.style.removeProperty( property );
	} else {
		element.style.removeAttribute( property );
	}
};

/**
 * @description Sets inline style property
 * @param element {HTMLElement}
 * @param property {String}
 * @param value {String|Number}
 * @param important {Boolean}
 */
export const setInlineStyleProperty = ( element: HTMLDivElement, property: string, value: any, important: boolean = false ): void => {
	if ( Type.isElement( element ) ) {
		if ( Type.isEmptyString( property ) ) return;
		if ( Type.isEmptyString( value ) || Type.isUndefined( value ) ) return;
		element.style.setProperty( property, value, ( important === true ? 'important' : undefined ) );
	}
};

/**
 * @description Returns transition for inline CSS
 * @param property {String} - i.e. 'width', default: 'all'
 * @param duration {String} - i.e. '0.1s', default: '0.2s'
 * @param timingFunction {String} - i.e. 'ease-out', default: 'ease'
 * @param delay {String} - i.e. '0.1s', default: '0s'
 * @return {String}
 * @example
 * ```typescript
 * var myElementString = '<div style="' + ccTransitionInline( 'width', '0.3s', ease-out , '0s' ) + ' width: 100px; height: 100px; "></div>;'
 * ```
 */
export const ccTransitionInline = ( property: string, duration: string | number, timingFunction: string, delay: string ): string => {
	const vendors = [ '-webkit-', '-moz-', '-o-', '-ms-', '' ];
	let out = '';
	for ( let i = 0; i < vendors.length; i++ ) {
		out += vendors[ i ] + 'transition:';
		out += ' ';
		out += property || 'all';
		out += ' ';
		out += duration || '0.2s';
		out += ' ';
		out += timingFunction || 'ease';
		out += ' ';
		out += delay || '0s';
		out += ';';
	}
	return out;
};

/**
 * @description Sets CSS3 style on element
 * @param element {HTMLElement}
 * @param property {String}
 * @param value {String}
 * @example
 * ```typescript
 * setCSS3Style( myElement, 'transition', 'opacity 1s' );
 * ```
 */
export const setCSS3Style = ( element: HTMLElement, property: string, value: string ): void => {
	const vendors = [ '-moz-', '-webkit-', '-o-', '-ms-', '-khtml-', '' ];
	const toCamelCase = ( str ) => {
		return str.toLowerCase().replace(
			/(\-[a-z])/g,
			( $1 ) => {
				return $1.toUpperCase().replace( '-', '' );
			}
		);
	};

	for ( let i = 0, l = vendors.length; i < l; i++ ) {
		element.style[ toCamelCase( vendors[ i ] + property ) ] = value;
	}
};

/**
 * @description Get computed style
 * @param element {HTMLElement}
 * @param pel {String} A string specifying the pseudo-element to match. Must be omitted (or null) for regular elements.
 * @return {CSSStyleDeclaration}
 */
export const cStyle = ( element: HTMLElement, pel: string | undefined = undefined ): CSSStyleDeclaration => {
	return window.getComputedStyle( element, ( pel || null ) );
};

/**
 * @description Get element.offset() similar to jQuery
 * @param element {HTMLElement}
 * @return {{top: Number, left: Number}}
 */
export const offset = ( element: HTMLElement ): { top: number, left: number } => {
	const rect = element.getBoundingClientRect();
	return {
		top: rect.top + document.body.scrollTop,
		left: rect.left + document.body.scrollLeft
	};
};

/**
 * @description Check CSS transition events API existence per browser and return it
 * @copyright David Walsh
 * @see {@link http://davidwalsh.name/css-animation-callback}
 * @return {String|null}
 */
export const whichTransitionEvent = (): string | null => {
	// sanity check
	if ( !Type.isFunction( document?.createElement ) ) {
		return null;
	}

	const fakeElement = document.createElement( 'fakeelement' ); // fake element
	// list of possible transitions
	const transitions = {
		'transition': 'transitionend',
		'OTransition': 'oTransitionEnd',
		'MozTransition': 'transitionend',
		'WebkitTransition': 'webkitTransitionEnd'
	};
	// loop through possible transitions
	for ( const key in transitions ) {
		if ( fakeElement.style[ key ] !== undefined ) { // if transition is found
			return transitions[ key ]; // return it
		}
	}
	// return null if no events found
	return null;
};

/**
 * @description Converts string to css class selector
 * @param {string} cssClassString
 * @return {string}
 * @example
 * console.log( stringToSelector( 'controls-bottom-btn btn-fast-forward normal' ) );
 * // output '.controls-bottom-btn.btn-fast-forward.normal'
 */
export const stringToClassSelector = ( cssClassString: string = '' ): string => {
	let output: string = cssClassString.split( ' ' ).join( '.' );
	if ( Type.isString( output ) && output.length > 0 && output.charAt( 0 ) !== '.' ) {
		output = `.${output}`;
	}
	return output.length > 1 ? output : undefined;
};

/**
 * @description CSS classes to be added or removed depending on if the min/max width/height criteria are met
 */
export interface ICssBreakpoint {
	minWidth?: number;
	maxWidth?: number;
	minHeight?: number;
	maxHeight?: number;
	className: string;
}

/**
 * @description Hiding a group of elements applying inline style. + removing inline stype to show them
 * @param {HTMLElement[]} elements
 * @returns {Promise<IPromiseResolveGeneric>}
 */
export const inlineOpacityHide = ( elements: HTMLElement[] ): Promise<IPromiseResolveGeneric> => {
	return new Promise(
		( resolve, reject ) => {
			elements.forEach(
				( element ) => {
					if ( Type.isElement( element ) ) {
						element.style.setProperty( 'opacity', '0', 'important' );
					}
				}
			);
			resolve( { msg: 'Done.' } );
		}
	);
};

/**
 * @description Removing inline opacity
 * @param {HTMLElement[]} elements
 * @returns {Promise<IPromiseResolveGeneric>}
 */
export const inlineOpacityRemove = ( elements: HTMLElement[] ): Promise<IPromiseResolveGeneric> => {
	return new Promise(
		( resolve, reject ) => {
			elements.forEach(
				( element ) => {
					if ( Type.isElement( element ) ) {
						element.style.removeProperty( 'opacity' );
					}
				}
			);
			resolve( { msg: 'Done.' } );
		}
	);
};